import React from 'react';
import styled from 'styled-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Tooltip, defaultTooltipIcon } from 'design-system/src/components/Tooltip/Tooltip';
import { ExpiringPriorAuthTableResponseRow, ExpiringPriorAuthTableRow, client } from '../api';
import PageContainer from '../components/PageContainer';
import { Outlet } from 'react-router-dom';
import { expiringPriorAuthsQueryKey } from '../queries';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';

import ExpiringPriorAuthTable from '../components/pas/ExpiringPriorAuthTable';
import { formatStandardDate } from 'shared-utils/utils/formatDate';

const Subtitle = styled.div`
  display: flex;
  gap: 4px;
`;

const ExpiringPriorAuthTablePage: React.FC = () => {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery<ExpiringPriorAuthTableRow[]>({
    queryKey: expiringPriorAuthsQueryKey(),
    queryFn: () => {
      return client.get(`expiring-prior-auths`).then((res) => {
        return res.data.map((item: ExpiringPriorAuthTableResponseRow) => ({
          ...item,
          formatted_approval_until: formatStandardDate(item.pa.approval_until),
          formatted_prescription_recieved_at: formatStandardDate(item.rx.prescription_recieved_at),
          formatted_dob: formatStandardDate(item.rx.dob),
        }));
      });
    },
  });

  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';
  const onDismiss = async (expiringPriorAuthId: string) => {
    queryClient.setQueryData(expiringPriorAuthsQueryKey(), (oldData: ExpiringPriorAuthTableRow[] | undefined) => {
      if (!oldData) return [];
      return oldData.map((row) => {
        if (row.pa.id === expiringPriorAuthId) {
          return { ...row, pa: { ...row.pa, dismissed: true } };
        }
        return row;
      });
    });

    if (isTrainingMode) {
      console.warn(`Training Mode: Mocking dismiss for PA ID: ${expiringPriorAuthId}`);
      return Promise.resolve({ success: true, dismissed: true });
    }

    await client.post(`prior-auths/${expiringPriorAuthId}/dismiss`);
  };

  const onRestore = async (expiringPriorAuthId: string) => {
    queryClient.setQueryData(expiringPriorAuthsQueryKey(), (oldData: ExpiringPriorAuthTableRow[] | undefined) => {
      if (!oldData) return [];
      return oldData.map((row) => {
        if (row.pa.id === expiringPriorAuthId) {
          return { ...row, pa: { ...row.pa, dismissed: false } };
        }
        return row;
      });
    });

    await client.post(`prior-auths/${expiringPriorAuthId}/restore`);
  };

  let body;
  if (isLoading) {
    body = <LoadingSpinner />;
  } else if (error) {
    body = <PageLoadError />;
  } else if (data) {
    body = <ExpiringPriorAuthTable data={data} onDismiss={onDismiss} onRestore={onRestore} />;
  }

  const subtitle = (
    <Subtitle>
      Prior authorizations expiring in the next 30 days.
      <Tooltip
        content="Note: This list may be missing a small percentage of prior authorizations for which Tandem does not have an expiration date on file."
        childNotInteractive
      >
        {defaultTooltipIcon}
      </Tooltip>
    </Subtitle>
  );

  return (
    <>
      <PageContainer title="Expiring Prior Authorizations" subtitle={subtitle}>
        {body}
      </PageContainer>
      <Outlet />
    </>
  );
};

export default ExpiringPriorAuthTablePage;
